import type {
  DataProvider,
  FormPayload,
  FormStructure,
  PreviewDataProviderPayload,
  SavOqNameValueParam,
  SavOqProvider,
  SavOqQueryJson,
} from '../types/types';

import { isSavOqProvider } from './typeGuards';

/** API wire shape for DATASTORE provider (`datastore` holds mapping + request fields). */
export interface SavOqWirePayload {
  type: 'DATASTORE';
  name?: string;
  description?: string;
  dependencies?: Record<string, string[]>;
  datastore: {
    mapping: Array<{ value: string; label: string }>;
    templateId: string;
    distinct: boolean;
    baseParams: SavOqNameValueParam[];
    fields: string[];
    rqbFilters: SavOqQueryJson;
  };
}

function asRecord(v: unknown): Record<string, unknown> | null {
  if (v !== null && typeof v === 'object' && !Array.isArray(v)) {
    return v as Record<string, unknown>;
  }

  return null;
}

/**
 * Flatten wire-format SavOQ provider (from API) into in-app {@link SavOqProvider}.
 */
export function deserializeSavOqProviderFromWire(
  raw: Record<string, unknown>
): SavOqProvider {
  const nested = asRecord(raw.datastore ?? raw.savOq);
  const mapping = nested && Array.isArray(nested.mapping) ? nested.mapping : [];
  const first = asRecord(mapping[0]);
  const legacySavOqRequest = nested?.savOqRequest;
  const legacyReq =
    legacySavOqRequest !== null &&
    typeof legacySavOqRequest === 'object' &&
    !Array.isArray(legacySavOqRequest)
      ? asRecord(legacySavOqRequest as Record<string, unknown>)
      : null;
  const req = legacyReq ?? nested ?? {};

  const valueField = String(first?.value ?? '');
  const labelField = String(first?.label ?? '');
  const templateId = String(req.templateId ?? req.objectKey ?? '');

  const rqbRaw = req.rqbFilters ?? req.fieldExpressions;
  const query: SavOqQueryJson =
    rqbRaw !== null && typeof rqbRaw === 'object' && !Array.isArray(rqbRaw)
      ? (rqbRaw as SavOqQueryJson)
      : {};

  const params = Array.isArray(req.baseParams ?? req.params)
    ? ((req.baseParams ?? req.params) as SavOqNameValueParam[])
    : [];

  return {
    type: 'DATASTORE',
    name: typeof raw.name === 'string' ? raw.name : undefined,
    description:
      typeof raw.description === 'string' ? raw.description : undefined,
    templateId,
    labelField,
    valueField,
    query,
    distinct: Boolean(req.distinct),
    params,
    pageSize: typeof raw.pageSize === 'number' ? raw.pageSize : undefined,
    dependencies:
      raw.dependencies && typeof raw.dependencies === 'object'
        ? (raw.dependencies as Record<string, string[]>)
        : undefined,
  };
}

function isSavOqWireShape(dp: unknown): boolean {
  const o = asRecord(dp);

  if (!o || o.type !== 'DATASTORE') {
    return false;
  }

  return (
    ('datastore' in o && asRecord(o.datastore) != null) ||
    ('savOq' in o && asRecord(o.savOq) != null)
  );
}

/**
 * Normalize a single data provider after load (wire SavOQ → flat {@link SavOqProvider}).
 */
export function normalizeDataProviderFromApi(dp: unknown): DataProvider {
  if (isSavOqWireShape(dp)) {
    return deserializeSavOqProviderFromWire(dp as Record<string, unknown>);
  }

  return dp as DataProvider;
}

/**
 * Normalize all data providers from API `formData.dataProviders`.
 */
export function normalizeDataProvidersFromApi(
  record: Record<string, DataProvider> | undefined | null
): Record<string, DataProvider> {
  if (!record) {
    return {};
  }

  return Object.fromEntries(
    Object.entries(record).map(([id, v]) => [
      id,
      normalizeDataProviderFromApi(v),
    ])
  );
}

export function normalizeFormPayloadSavOqInbound(
  payload: FormPayload
): FormPayload {
  const fd = payload.formData;

  if (!fd?.dataProviders) {
    return payload;
  }

  return {
    ...payload,
    formData: {
      ...fd,
      dataProviders: normalizeDataProvidersFromApi(fd.dataProviders),
    },
  };
}

/**
 * Serialize flat {@link SavOqProvider} to API wire shape (nested `datastore`).
 */
export function serializeSavOqProviderForApi(
  dp: SavOqProvider
): SavOqWirePayload {
  const params = (dp.params ?? []).filter(
    (p) =>
      String(p.name ?? '').trim() !== '' || String(p.value ?? '').trim() !== ''
  );

  const fields = [dp.valueField, dp.labelField].filter(
    (f) => String(f ?? '').trim() !== ''
  );

  const rqbFilters: SavOqQueryJson =
    dp.query && typeof dp.query === 'object' ? dp.query : {};

  const mapping: Array<{ value: string; label: string }> = [];

  if (dp.valueField || dp.labelField) {
    mapping.push({
      value: dp.valueField ?? '',
      label: dp.labelField ?? '',
    });
  }

  const base: SavOqWirePayload = {
    type: 'DATASTORE',
    name: dp.name,
    datastore: {
      mapping,
      templateId: dp.templateId,
      distinct: dp.distinct ?? false,
      baseParams: params,
      fields,
      rqbFilters,
    },
  };

  if (dp.description) {
    base.description = dp.description;
  }

  if (dp.dependencies && Object.keys(dp.dependencies).length > 0) {
    base.dependencies = dp.dependencies;
  }

  return base;
}

/**
 * Replace each SavOQ entry with wire format; leave other provider types unchanged.
 */
export function serializeDataProvidersForApi(
  record: Record<string, DataProvider>
): Record<string, DataProvider | SavOqWirePayload> {
  return Object.fromEntries(
    Object.entries(record).map(([k, v]) => [
      k,
      isSavOqProvider(v) ? serializeSavOqProviderForApi(v) : v,
    ])
  );
}

export function withWireSavOqFormPayload(
  formPayload: FormPayload
): FormPayload {
  const fd = formPayload.formData;

  if (!fd?.dataProviders) {
    return formPayload;
  }

  return {
    ...formPayload,
    formData: {
      ...fd,
      /* Wire SavOQ shape is not assignable to in-app DataProvider; JSON body matches backend contract. */
      dataProviders: serializeDataProvidersForApi(
        fd.dataProviders
      ) as unknown as FormStructure['dataProviders'],
    },
  };
}

/** Cast: preview API accepts wire-shaped SavOQ objects in `dataProviders`. */
export function serializePreviewPayloadForApi(
  payload: PreviewDataProviderPayload
): PreviewDataProviderPayload {
  return {
    ...payload,
    form: {
      ...payload.form,
      dataProviders: serializeDataProvidersForApi(
        payload.form.dataProviders
      ) as Record<string, DataProvider>,
    },
  };
}
